/**
 * PM-6 — derive the override-block set + per-field before/after diffs
 * (PM-D18) from an analyze report. Pure helper, shared by `Step5Commit`
 * (counts / gate) and `OverrideConfirmDialog` (the diff table). Kept out of
 * the components so it is unit-testable without rendering.
 *
 * Only INCLUDED rows are considered — an excluded row never commits, so it
 * cannot block or be overridden.
 */
import { IMPORT_FIELD_LABELS } from "./types.js";
import type {
  AnalyzeImportReport,
  ImportAnalyzedRow,
  ImportColumnMapping,
  ImportFieldDiff,
  ImportFieldKey,
} from "./types.js";

/** Does an INCLUDED row carry any `hard_error` finding? */
export function rowHasHardError(row: ImportAnalyzedRow): boolean {
  return row.messages.some((m) => m.severity === "hard_error");
}

/** Does an INCLUDED row carry any `override_block` finding? */
export function rowHasOverrideBlock(row: ImportAnalyzedRow): boolean {
  return row.messages.some((m) => m.severity === "override_block");
}

/** The flattened per-field before/after diffs (PM-D18) carried by a row's
 *  `override_block` messages — the data behind the FB-27 "which parameter
 *  differs" surfacing. Empty when the row has no override_block diff. Used by
 *  the Data grid + the Review tree to show the diff inline (no dialog). */
export function rowOverrideDiffs(row: ImportAnalyzedRow): ImportFieldDiff[] {
  const diffs: ImportFieldDiff[] = [];
  for (const m of row.messages) {
    if (m.severity === "override_block" && m.diff) diffs.push(...m.diff);
  }
  return diffs;
}

/**
 * FB-30 — the GRID COLUMN indexes a row's override-block diff fields map to,
 * via the current column→field mapping. Each `message.diff[].field` is an
 * import field key (`siteName`, `latitude`, …) which the mapping pins to a
 * raw column index; these are the exact cells to highlight yellow (the
 * mismatch cells). A diff field with no mapped column (defensive) is skipped.
 * Returns an empty set for a row with no override-block diff.
 */
export function rowMismatchColumns(
  row: ImportAnalyzedRow,
  mapping: readonly ImportColumnMapping[],
): Set<number> {
  const columnByField = new Map<string, number>();
  for (const m of mapping) {
    if (m.field !== null) columnByField.set(m.field, m.columnIndex);
  }
  const cols = new Set<number>();
  for (const d of rowOverrideDiffs(row)) {
    const col = columnByField.get(d.field);
    if (col !== undefined) cols.add(col);
  }
  return cols;
}

/* ── FB-32: per-CELL override (per (rowIndex, field), not per row) ──────── */

/** FB-32 — the stable key for a per-cell override mark: a `(rowIndex, field)`
 *  pair. The wizard's override state is a `Set<string>` of these; the commit
 *  body's `overrideFields` is the parsed form. */
export function overrideFieldKey(rowIndex: number, field: string): string {
  return `${String(rowIndex)}:${field}`;
}

/** FB-32 — parse an `overrideFieldKey` back to its `(rowIndex, field)`. The
 *  field may itself contain `:` (none of the import field keys do today, but
 *  be defensive) — split only on the FIRST colon. */
export function parseOverrideFieldKey(
  key: string,
): { rowIndex: number; field: string } | null {
  const sep = key.indexOf(":");
  if (sep < 0) return null;
  const rowIndex = Number(key.slice(0, sep));
  const field = key.slice(sep + 1);
  if (Number.isNaN(rowIndex) || field.length === 0) return null;
  return { rowIndex, field };
}

/**
 * FB-32 — resolve a clicked grid COLUMN back to the override-block diff FIELD
 * it represents on a row, via the column→field mapping. The reverse of
 * {@link rowMismatchColumns}: a yellow mismatch cell sits on a column; that
 * column maps to a field; that field must be one of the row's `message.diff`
 * fields. Returns the field key when the cell is a genuine per-field mismatch,
 * else `null` (a non-diff cell, or an unmapped column).
 */
export function mismatchFieldForColumn(
  row: ImportAnalyzedRow,
  mapping: readonly ImportColumnMapping[],
  columnIndex: number,
): string | null {
  const field = mapping.find((m) => m.columnIndex === columnIndex)?.field;
  if (!field) return null;
  return rowOverrideDiffs(row).some((d) => d.field === field) ? field : null;
}

/** Human label for an override diff field — maps the wire field keys
 *  (`systemType`, `bladeType`, `latitude`, …) to the same labels the mapping
 *  picker uses, falling back to the raw key for unknowns. */
export function diffFieldLabel(field: string): string {
  return Object.prototype.hasOwnProperty.call(IMPORT_FIELD_LABELS, field)
    ? IMPORT_FIELD_LABELS[field as ImportFieldKey]
    : field;
}

/** Render an override diff value for inline display: blank/empty ⇒ "(blank)". */
export function diffValueText(v: unknown): string {
  if (v === null || v === undefined) return "(blank)";
  if (typeof v === "string") return v.trim().length > 0 ? v : "(blank)";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

/** One-line "Label: existing X → sheet Y" summary for a single field diff. */
export function diffLine(d: ImportFieldDiff): string {
  return `${diffFieldLabel(d.field)}: existing ${diffValueText(
    d.current,
  )} → sheet ${diffValueText(d.incoming)}`;
}

export interface ImportCommitCounts {
  /** Rows that would be created fresh (no blocking/confirm finding). */
  created: number;
  /** Existing turbines that match unchanged (info `matched`/no divergence). */
  matchedUnchanged: number;
  /** Included rows blocked by an existing-turbine divergence (PM-D4). */
  overrideBlock: number;
  /** Included rows with a hard error — gate the whole commit (PM-D19). */
  hardError: number;
  /** Total included rows. */
  included: number;
}

const MATCHED_CODES = new Set(["matched", "matched_unchanged", "exists"]);

/**
 * Summary counts of the four pre-commit buckets (Step 5 review). Derived
 * purely from the included rows' findings:
 *  - hardError      — any `hard_error` finding (gates the batch).
 *  - overrideBlock  — any `override_block` finding (would change existing).
 *  - matchedUnchanged — an explicit `matched`/`exists` info finding.
 *  - created        — everything else (a fresh turbine).
 * A row is counted in the FIRST bucket it qualifies for (hard_error wins),
 * so the buckets partition the included rows.
 */
export function deriveCommitCounts(
  report: AnalyzeImportReport,
): ImportCommitCounts {
  let created = 0;
  let matchedUnchanged = 0;
  let overrideBlock = 0;
  let hardError = 0;
  let included = 0;

  for (const r of report.rows) {
    if (!r.included) continue;
    included += 1;
    if (rowHasHardError(r)) {
      hardError += 1;
    } else if (rowHasOverrideBlock(r)) {
      overrideBlock += 1;
    } else if (r.messages.some((m) => MATCHED_CODES.has(m.code))) {
      matchedUnchanged += 1;
    } else {
      created += 1;
    }
  }

  return { created, matchedUnchanged, overrideBlock, hardError, included };
}

export interface OverrideRowDiff {
  rowIndex: number;
  /** Identifier cell value if it can be resolved, else the 1-based row no. */
  label: string;
  diffs: ImportFieldDiff[];
}

/**
 * The INCLUDED `override_block` rows + their flattened per-field diffs
 * (PM-D18), for the admin override dialog. A row's label is its mapped
 * `identifier` cell value when resolvable (mapping → columnIndex → cell),
 * else a 1-based row label.
 */
export function collectOverrideRowDiffs(
  report: AnalyzeImportReport,
): OverrideRowDiff[] {
  const identifierColumn =
    report.mapping.find((m) => m.field === "identifier")?.columnIndex ?? null;

  const out: OverrideRowDiff[] = [];
  for (const r of report.rows) {
    if (!r.included || !rowHasOverrideBlock(r)) continue;
    const diffs: ImportFieldDiff[] = [];
    for (const m of r.messages) {
      if (m.severity === "override_block" && m.diff) {
        diffs.push(...m.diff);
      }
    }
    const idCell =
      identifierColumn !== null
        ? r.cells.find((c) => c.columnIndex === identifierColumn)?.value
        : null;
    const label =
      idCell && idCell.trim().length > 0
        ? idCell.trim()
        : `Row ${String(r.rowIndex + 1)}`;
    out.push({ rowIndex: r.rowIndex, label, diffs });
  }
  return out;
}
